import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Icon,
  Modal,
  Upload,
  UploadFileType,
  UploadChangeParamType,
  message,
} from 'components';
import { PhotoFileType, PhotoFileStatus } from '../../types';
import PhotoPreviewModal, { PreviewPhoto } from './PhotoPreviewModal';

interface PhotoUploadModal {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  loadPhotos: (photos: PhotoFileType[]) => void;
  imagesTypes: string[];
}

const PhotoUploadModal: React.FC<PhotoUploadModal> = ({
  visible,
  setVisible,
  loadPhotos,
  imagesTypes,
}) => {
  const [photos, setPhotos] = useState<Record<string, UploadFileType>>({});
  const [previewImage, setPreviewImage] = useState<PreviewPhoto | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState<string>('');
  const [disbleDoneBtn, setDisableDoneBtn] = useState<boolean>(false);

  const photosArray = useMemo(() => Object.values(photos).filter(photo => photo.status !== 'removed'), [photos]);

  const uploading = useMemo(() => photosArray.some(photo => photo.status === 'uploading'), [photosArray]);

  const closePreview = () => {
    setPreviewImage(null);
    setPreviewOpen(false);
  };

  const closeModal = () => {
    setPhotos({});
    setVisible(false);
    closePreview();
  };

  const onUpload = ({ file }: UploadChangeParamType<UploadFileType<any>>) => {
    const newFile = { ...file };

    if (
      newFile.response?.status === 'error' ||
      newFile.status === 'error' ||
      newFile?.error?.status >= 400
    ) {
      const errorDetails =
        newFile.response?.message || newFile.response || 'Upload Error';
      message.error(`Error uploading file: ${newFile.name}. ${errorDetails}`);
      newFile.status = 'error';
      newFile.response = errorDetails;
    } else if (newFile.status === 'done' && !newFile.response?.url) {
      newFile.status = 'uploading';
    }
    if (!newFile.thumbUrl && newFile.response?.url) {
      newFile.thumbUrl = newFile.response?.url;
    }
    if (visible) {
      setPhotos(prevPhotos => ({ ...prevPhotos, [newFile.uid]: newFile }));
    }
  };

  useEffect(() => {
    let err = false;
    let incluidValidImage = false;
    photosArray.forEach(photo => {
      if (!imagesTypes.includes(`.${photo.type.split('/')[1]}`)) err = true;
      else incluidValidImage = true;
    });

    if (incluidValidImage) {
      setDisableDoneBtn(false);
    } else {
      setDisableDoneBtn(true);
    }

    if (err) {
      setError(
        'Invalid photo types could not be uploaded. Valid photo types are .jpg, .jpeg, .png, and .gif.',
      );
    } else {
      setError('');
    }
  }, [photosArray, imagesTypes]);

  const transferPhotos = () => {
    const formattedPhotos = photosArray
      .filter(
        ({ status, type }) =>
          status === 'done' && imagesTypes.includes(`.${type.split('/')[1]}`),
      )
      .map(({ name, type, response }) => ({
        filename: name,
        mimetype: type,
        url: response.url,
        status: 'done' as PhotoFileStatus,
      }));
    loadPhotos(formattedPhotos);
    closeModal();
  };

  const onPreview = (photo: UploadFileType) => {
    if (photo?.name && photo?.response?.url) {
      setPreviewImage({ name: photo.name, url: photo.response.url });
      setPreviewOpen(true);
    }
  };

  return (
    <Modal
      open={visible}
      title="Upload Listing Image"
      onCancel={closeModal}
      footer={[
        <Button key="cancelPhoto" type="default" onClick={closeModal}>
          Cancel
        </Button>,
        <Button
          key="submitPhoto"
          type="primary"
          loading={uploading}
          disabled={uploading || disbleDoneBtn}
          onClick={transferPhotos}
        >
          Done
        </Button>,
      ]}
    >
      <Upload
        action="/upload/single"
        listType="picture-card"
        accept={imagesTypes.join(',')}
        fileList={photosArray}
        onChange={onUpload}
        disabled={uploading}
        onPreview={onPreview}
        multiple
      >
        <div>
          <Icon type="plus" />
          <div style={{ marginTop: 8 }}>Upload</div>
        </div>
      </Upload>
      {error && <small style={{ color: '#f00' }}>{error}</small>}
      <PhotoPreviewModal
        visible={previewOpen}
        closeModal={closePreview}
        previewImage={previewImage}
      />
    </Modal>
  );
};

export default PhotoUploadModal;
